/**
 * Locked "Search By" matching rules for the parts selection screen (Awaiting Inventory, Lead,
 * Vehicle and Triage all render CreateJobs). Saved per browser in localStorage, the same way
 * TableFilterPanel saves the listing pages' default filters.
 *
 * Only the ticked rules are saved. The year range comes from each vehicle's own dates, so it
 * is never carried over to the next vehicle.
 */
export interface ISearchByDefaults {
  bodySearchBy: string[];
  mechSearchBy: string[];
  categoryStatusSearchBy: string[];
}

export type SearchByAllowedValues = Record<keyof ISearchByDefaults, string[]>;

export const SEARCH_BY_DEFAULTS_STORAGE_KEY = "wilma:searchByFilters:create-jobs";

const SEARCH_BY_KEYS: (keyof ISearchByDefaults)[] = ["bodySearchBy", "mechSearchBy", "categoryStatusSearchBy"];

/**
 * Reads saved rules back, or null when there's nothing usable. Values that are no longer
 * offered as options are dropped, so a renamed or removed rule can't be sent to the API.
 */
export function parseSearchByDefaults(raw: string | null, allowed: SearchByAllowedValues): ISearchByDefaults | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== "object" || parsed === null) return null;

  const saved = parsed as Record<string, unknown>;
  const result = {} as ISearchByDefaults;

  for (const key of SEARCH_BY_KEYS) {
    const values = saved[key];
    if (!Array.isArray(values) || !values.every((value) => typeof value === "string")) return null;

    const allowedSet = new Set(allowed[key]);
    result[key] = Array.from(new Set(values as string[])).filter((value) => allowedSet.has(value));
  }

  return result;
}

export function loadSearchByDefaults(allowed: SearchByAllowedValues): ISearchByDefaults | null {
  try {
    return parseSearchByDefaults(localStorage.getItem(SEARCH_BY_DEFAULTS_STORAGE_KEY), allowed);
  } catch {
    return null;
  }
}

export function saveSearchByDefaults(defaults: ISearchByDefaults) {
  // Picked field by field so nothing else on the object (e.g. a year range) gets saved too.
  const { bodySearchBy, mechSearchBy, categoryStatusSearchBy } = defaults;

  try {
    localStorage.setItem(SEARCH_BY_DEFAULTS_STORAGE_KEY, JSON.stringify({ bodySearchBy, mechSearchBy, categoryStatusSearchBy }));
  } catch {
    // Storage blocked or full - the rules still apply to this page, they just won't stick.
  }
}

export function clearSearchByDefaults() {
  try {
    localStorage.removeItem(SEARCH_BY_DEFAULTS_STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
